import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { EventItem, AnnouncementItem } from "./BoardItems";
import { AddToCalendar, SlotList } from "./EventExtras";
import { VolunteerSection } from "./VolunteerRoles";
import { MakeupPoll } from "./MakeupPoll";

const STATUSES = [
  { value: "yes", label: "Going" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Can't" },
];

const Rsvp = ({ code, event, child, mine, onDone }) => (
  <div className="space-y-1.5" data-testid="event-rsvp">
    <span className="ctl-label">{child ? `RSVP for ${child}` : "Enter your child's name above to RSVP"}</span>
    <div className="seg-group w-full">
      {STATUSES.map((s) => (
        <button key={s.value} disabled={!child} data-testid={`rsvp-${s.value}`}
          className={`seg-btn flex-1 ${mine === s.value ? "seg-btn-active" : ""}`}
          onClick={() => api.rsvp(code, event.id, { child_name: child, status: s.value }).then((e) => onDone(e, s.value), (err) => toast.error(formatApiError(err)))}>
          {s.label}
        </button>
      ))}
    </div>
  </div>
);

export const BoardView = ({ code, teamName }) => {
  const store = `dp-rsvp-${code.toUpperCase()}`;
  const saved = JSON.parse(localStorage.getItem(store) || '{"child":"","mine":{}}');
  const [board, setBoard] = useState(null);
  const [child, setChild] = useState(saved.child);
  const [draft, setDraft] = useState(saved.child);
  const [mine, setMine] = useState(saved.mine);

  useEffect(() => { api.board(code).then(setBoard).catch(() => setBoard({ announcements: [], events: [] })); }, [code]);
  useEffect(() => {
    localStorage.setItem(store, JSON.stringify({ child, mine }));
    window.dispatchEvent(new Event("dp-board-change"));
  }, [store, child, mine]);

  const saveChild = (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (name !== child) setMine({});
    setChild(name);
  };
  const done = (ev, status) => {
    setBoard((b) => ({ ...b, events: b.events.map((x) => (x.id === ev.id ? ev : x)) }));
    setMine((m) => ({ ...m, [ev.id]: status }));
    toast.success("RSVP saved");
  };

  const replace = (ev) => setBoard((b) => ({ ...b, events: b.events.map((x) => (x.id === ev.id ? ev : x)) }));
  const slotAction = (fn, msg) => fn.then((ev) => { replace(ev); toast.success(msg); }, (err) => toast.error(formatApiError(err)));

  const roleAction = (fn, msg) => fn.then((v) => {
    setBoard((b) => ({ ...b, volunteers: v }));
    window.dispatchEvent(new Event("dp-board-change"));
    toast.success(msg);
  }, (err) => toast.error(formatApiError(err)));
  const vote = (e, o) => api.makeupVote(code, e.id, { child_name: child, option_id: o.id }).then((ev) => {
    replace(ev);
    setMine((m) => ({ ...m, [`mk:${e.id}`]: o.id }));
    toast.success("Vote saved");
  }, (err) => toast.error(formatApiError(err)));

  if (!board) return <div className="card">Loading schedule...</div>;
  return (
    <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr] items-start" data-testid="board-view">
      <section className="space-y-3">
        <h2 className="font-heading text-3xl font-extrabold uppercase">Upcoming schedule</h2>
        <form className="card !p-3 flex gap-2" onSubmit={saveChild}>
          <input data-testid="rsvp-child-input" className="field-input flex-1 min-w-0" maxLength={40} placeholder="Your child's name" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button className="btn-primary" data-testid="rsvp-child-save">{child ? "Change" : "Set"}</button>
        </form>
        {board.events.length === 0 && <div className="card font-semibold" data-testid="events-empty">No upcoming events yet.</div>}
        {board.events.map((e) => (
          <EventItem key={e.id} event={e}>
            <MakeupPoll event={e} child={child} mine={mine[`mk:${e.id}`]} onVote={(o) => vote(e, o)} />
            {e.status !== "canceled" && <Rsvp code={code} event={e} child={child} mine={mine[e.id]} onDone={done} />}
            {e.status !== "canceled" && e.slots.length > 0 && (
              <SlotList slots={e.slots} me={child}
                onSign={(s) => slotAction(api.slotSignup(code, e.id, s.id, child), `Signed up for ${s.label}`)}
                onCancel={(s) => slotAction(api.slotCancel(code, e.id, s.id, child), `Removed from ${s.label}`)} />
            )}
            {e.status !== "canceled" && <AddToCalendar event={e} teamName={teamName} />}
          </EventItem>
        ))}
      </section>
      <div className="space-y-6">
        <section className="space-y-3">
          <h2 className="font-heading text-3xl font-extrabold uppercase">Team news</h2>
          {board.announcements.length === 0 && <div className="card font-semibold" data-testid="announcements-empty">No announcements yet.</div>}
          {board.announcements.map((a) => <AnnouncementItem key={a.id} a={a} />)}
        </section>
        <VolunteerSection roles={board.volunteers || []} me={child}
          onSign={(r) => roleAction(api.volunteerSignup(code, r.id, child), `Signed up as ${r.label}`)}
          onCancel={(r) => roleAction(api.volunteerCancel(code, r.id, child), `Removed from ${r.label}`)} />
      </div>
    </div>
  );
};
